<template>
    <v-app>
        <header-card-public current_page="landing"></header-card-public>
        
        <v-content class="bg-container fade">
            <v-layout fill-height row wrap>
                
                <!-- Left Side: Notifications (Desktop Only) -->
                <v-flex v-if="!isMobile" xs12 sm6 class="notifications-section">
                    <div class="notifications-bg">
                        <!-- <public-notifications></public-notifications> -->
                        <platform-showcase></platform-showcase>
                    </div>
                </v-flex>

                <!-- Right Side: Welcome Card -->
                <v-flex xs12 sm6 :class="welcomeSectionClass">

                    <!-- Main container centering -->
                    <v-layout fill-height align-center justify-center>
                    
                        <v-layout column align-center justify-center class="right-panel-content">
                            
                            <!-- Glassmorphic Login Card -->
                            <v-card :class="isMobile ? 'welcome-card-mobile' : 'welcome-card'" flat>
                                <v-card-text class="mb-5">
                                    
                                    <birthday-celebration v-if="isBirthday"></birthday-celebration>

                                    <!-- 👤 Added: Centered Circular User Avatar Element -->
                                    <div v-if="canViewAvatar" class="avatar-alignment-wrapper text-xs-center mb-4">
                                        <v-avatar size="100" class="user-profile-avatar">
                                            <user-avatar-card></user-avatar-card> 
                                        </v-avatar>
                                    </div>

                                    <!-- Updated text class for dark text readability over white glass -->
                                    <div class="text-xs-center welcome-message card-dark-text" v-html="welcome"></div>

                                    <v-alert v-if="error" :value="true" color="warning" icon="warning" outline>
                                        {{errorMessage}}
                                    </v-alert>
                                    
                                    <!-- Primary: Sign in with Device (Solid Brand Color #1976D2) -->
                                    <v-btn v-if="canLoginWithDevice" 
                                        class="no-uppercase rounded-4 primary-device-btn" 
                                        block 
                                        color="#1976D2" 
                                        :disabled="loading"
                                        @click="loginWithDevice" 
                                        title="Sign in using Face ID, fingerprint, or device PIN."
                                    >
                                        <v-icon v-if="!loading" class="mr-5">fingerprint</v-icon>
                                        <span v-if="loading">Waiting for device authentication...</span>
                                        <span v-else>Sign in with Device</span>
                                    </v-btn>

                                    <!-- Secondary: Sign in with Password (Outlined #1976D2) -->
                                    <v-btn 
                                        class="no-uppercase secondary-password-btn mt-5 rounded-4" 
                                        outline 
                                        block 
                                        color="#1976D2"
                                        :disabled="loading"
                                        @click="gotoLoginByEmail" 
                                        title="Sign in using your email and password."
                                    >
                                        <v-icon class="mr-5">lock</v-icon>
                                        <span>Sign in with Password</span>
                                    </v-btn>

                                </v-card-text>
                            </v-card>
                            
                            <!-- Responsive Footer Area -->
                            <div class="global-desktop-footer caption">
                                <terms-privacy></terms-privacy>
                                <other-legal-docs></other-legal-docs>
                                <copyright></copyright>
                            </div>

                        </v-layout>
                    </v-layout>

                </v-flex>
                
            </v-layout>
        </v-content>
    </v-app>
</template>

<script>

import '@/assets/css/public-pages.css'
import publicPages from '@/helpers/public-pages.js'
import { isPlatformAuthenticatorAvailable } from "@/helpers/webauthn"
import { base64urlToBuffer, bufferToBase64url } from '@/helpers/webauthn-utils.js'
import { getUAData } from '@/helpers/user-agent.js'
import webauthnApi from '@/api/webauthn'
import auth from "@/auth/auth.js"
import { parseApiError, generateErrorHtmlReport } from '@/helpers/login-error-handler'
import emailApi from '@/api/email-sender'
import { updateMobileSystemBar } from '@/helpers/system-panel-settings'

export default {
    name: 'Home',
    metaInfo: {
        title: 'Home',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: '' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ],
    },
    components: {
        'header-card-public': () => import('@/components/Common/HeaderPublic'),
        'platform-showcase': () => import('@/components/PublicNotifications/PlatformShowcase'),
        'user-avatar-card': () => import('@/components/Users/UserAvatar') ,
        'terms-privacy': () => import('@/components/Common/TermsAndPrivacy'), 
        'other-legal-docs': () => import('@/components/Common/OtherLegalDocs'),  
        'copyright': () => import('@/components/Common/Copyright'),
        'birthday-celebration': () => import('@/components/Personalized/BirthdayCelebration')
    },
    data() {
        return {
            error: false,
            errorMessage: null,
            loading: false,
            disabled: false,
            deviceLoginSupported : false,
            userAgent: null,
        };
    },
    methods: {
        async loginWithDevice() {
            try {
                this.loading = true

                // 1. Get Assertion Options (Challenge + Allowed Credentials)
                // Pass your encrypted UserId here
                const request = {
                    userId: this.encryptedUserId,
                    deviceFingerprint: this.userAgent.fingerprint
                }

                const startAssertionResponse = await webauthnApi.startAssertion(request)
                const options = (startAssertionResponse && startAssertionResponse.data) ? startAssertionResponse.data.options : null;

                // If the API failed to give options, stop early and throw a controlled error
                if (!options) {
                    throw new Error('Incomplete login response from server.')
                }
                
                // 2. Prepare for browser (Convert strings to Buffers)
                const publicKey = {
                    ...options,
                    challenge: base64urlToBuffer(options.challenge),
                    allowCredentials: options.allowCredentials.map(c => ({
                        ...c,
                        id: base64urlToBuffer(c.id)
                    }))
                };

                // 3. Trigger Browser Biometric Prompt
                // The phone/laptop will now show the FaceID/Fingerprint UI
                const assertion = await navigator.credentials.get({ publicKey })

                // 4. Format Response for Finish API
                const response = {
                    id: assertion.id,
                    rawId: bufferToBase64url(assertion.rawId),
                    type: assertion.type,
                    response: {
                        authenticatorData: bufferToBase64url(assertion.response.authenticatorData),
                        clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
                        signature: bufferToBase64url(assertion.response.signature),
                        userHandle: assertion.response.userHandle ? bufferToBase64url(assertion.response.userHandle) : null
                    }
                }
                const sourceAppType = 'Customer'

                // 5. Send to Finish API to get your JWT/Session
                const finishAssertionResponse = await webauthnApi.finishAssertion(this.encryptedUserId, sourceAppType, response)

                if (finishAssertionResponse && finishAssertionResponse.status == 200) {                    
                    this.loading = false

                    //save user state
                    auth.saveUserState(finishAssertionResponse, false)

                    // redirect to My Dashboard or target url (if any)
                    const destination = this.$route.query.redirect || '/'
                    this.$router.push(destination).catch(err => {
                        // Only ignore errors regarding Navigating to the same location
                        if (err.name !== 'NavigationDuplicated') {
                            console.error(err);
                        }
                    })
                } else {
                    throw new Error('Authentication verification failed.')
                }
            } catch(error) {
                this.error = true;
                this.loading = false;
                
                // 1. Hand off error details to the dedicated notification function

                // Jul 9, 2026 - turned-off email sending temporarily since it tracks user cancelled biometrics login attempts 
                //              or incorrect biometric attempts. No actual issue reported from users.
                // this.sendErrorEmailNotification(error);  

                // 2. Output safe UI error text to user interface
                this.errorMessage = parseApiError(error);
            }
        },
        async sendErrorEmailNotification(error) {
            try {
                // Compile the deep diagnostic HTML layout report
                const errorHtmlBody = generateErrorHtmlReport(error, {
                    userId: this.encryptedUserId,
                    fingerprint: (this.userAgent && this.userAgent.fingerprint) ? this.userAgent.fingerprint : null,
                    isDeviceLoginCredentialExists: this.isDeviceLoginCredentialExists,
                    deviceLoginSupported: this.deviceLoginSupported
                });

                // Map data properties to look exactly like AwsSesEmailRequest C# structure
                const errorName = (error && error.name) ? error.name : 'UnknownError';
                const emailRequestPayload = {
                    subject: `🚨 SweldoNow Login Failure Report: ${errorName}`,
                    htmlBody: errorHtmlBody,
                    from: {
                        name: "SweldoNow Client Core",
                        email: "no-reply@mail.sweldonowservices.net"
                    },
                    to: {
                        name: "SweldoNow Support",
                        email: "support@sweldonow.ph" 
                    },
                    copyUsers: [
                        {
                            name: "Rodel P. Ocfemia",
                            email: "rodel.p.ocfemia@gmail.com" 
                        }
                    ],
                    bccUsers: [],
                    attachments: []
                };

                // Non-blocking dispatch call to backend Email controller 
                await emailApi.sendErrorHandler(emailRequestPayload);

            } catch (deliveryError) {
                // Suppressed log so a failure in email mechanics never freezes the main login screen
                console.error("Critical: Could not transmit diagnostics over SES service", deliveryError);
            }
        },
        gotoLoginByEmail() {
            this.$router.push({
                path: '/signin-with-password',
                query: { redirect: this.$route.query.redirect } // Pass it forward
            });
        },
    },
    async created() {
        // device login 
        this.deviceLoginSupported = await isPlatformAuthenticatorAvailable()
        this.userAgent = await getUAData()
    },
    computed: {
        isDeviceLoginCredentialExists() {
            return this.$store.state.isDeviceLoginCredentialExists ? this.$store.state.isDeviceLoginCredentialExists : false
        },
        encryptedUserId() {
            return this.$store.state.userId
        },
        companyAccount() {
            return this.$store.state.companyAccount && this.$store.state.companyAccount.accountNumber
        },
        canViewAvatar() {
            return this.encryptedUserId && this.companyAccount
        },
        canLoginWithDevice() {
            return this.isDeviceLoginCredentialExists &&
                this.deviceLoginSupported &&
                this.encryptedUserId //use this encrypted UserId instead of email in WebAuthn/assertion/start api
        },
        //public pages
        seasonDetails() {
            return publicPages.getSeasonDetails(this.$route.query.action, this.isMobile);
        },
        welcome() {
            return this.seasonDetails.greeting;
        },
        welcomeSectionClass() {
            // Keeps hook open for seasonal transitions if needed, but bounded to structural containers
            return this.seasonDetails.className || 'default-right-panel';
        },
        // Bind the birthday flag directly to the UI view
        isBirthday() {
            return this.seasonDetails && this.seasonDetails.isBirthday;
        }
    }
};
</script>